import { useState, useEffect } from 'react';
import { useSuperAdmin } from '../context/SuperAdminContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import EmailConfig from '../components/shared/EmailConfig';
import { 
  Shield, LogOut, Users, Building2, Database, Activity,
  Plus, Edit2, Trash2, CheckCircle, XCircle, RefreshCw,
  Loader2, Eye, Settings, BarChart3, Wallet, ArrowRightLeft, Star, Key, UserCog, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const SuperAdminDashboard = () => {
  const { admin, logout, API } = useSuperAdmin();
  const navigate = useNavigate();
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [dbConfigs, setDbConfigs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantForm, setTenantForm] = useState({ name: '', description: '' });
  const [savingTenant, setSavingTenant] = useState(false);
  
  const [dbDialogOpen, setDbDialogOpen] = useState(false);
  const [dbForm, setDbForm] = useState({ name: '', connection_string: '', database_name: '', is_default: false });
  const [savingDb, setSavingDb] = useState(false);
  
  const [tenantDetailOpen, setTenantDetailOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Migration states
  const [migratingDb, setMigratingDb] = useState(null);
  const [migrationProgress, setMigrationProgress] = useState(null);
  
  // Password change states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, tenantsRes, dbRes, usersRes] = await Promise.all([
        axios.get(`${API}/analytics`),
        axios.get(`${API}/tenants`),
        axios.get(`${API}/config/databases`),
        axios.get(`${API}/users`)
      ]);
      setAnalytics(analyticsRes.data);
      setTenants(tenantsRes.data);
      setDbConfigs(dbRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const assignUserToTenant = async (userId, tenantId) => {
    try {
      await axios.patch(`${API}/users/${userId}/tenant?tenant_id=${tenantId}`);
      toast.success('User assigned to tenant');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/super-admin');
  };

  // Tenant CRUD
  const openTenantDialog = (tenant = null) => {
    if (tenant) {
      setEditingTenant(tenant);
      setTenantForm({ name: tenant.name, description: tenant.description || '' });
    } else {
      setEditingTenant(null);
      setTenantForm({ name: '', description: '' });
    }
    setTenantDialogOpen(true);
  };

  const saveTenant = async (e) => {
    e.preventDefault();
    setSavingTenant(true);
    try {
      if (editingTenant) {
        await axios.patch(`${API}/tenants/${editingTenant.id}`, tenantForm);
        toast.success('Tenant updated');
      } else {
        await axios.post(`${API}/tenants`, tenantForm);
        toast.success('Tenant created');
      }
      setTenantDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tenant');
    } finally {
      setSavingTenant(false);
    }
  };

  const deleteTenant = async (tenant) => {
    if (!confirm(`Delete tenant "${tenant.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/tenants/${tenant.id}`);
      toast.success('Tenant deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const toggleTenantStatus = async (tenant) => {
    try {
      await axios.patch(`${API}/tenants/${tenant.id}`, { is_active: !tenant.is_active });
      toast.success(`Tenant ${tenant.is_active ? 'disabled' : 'enabled'}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update tenant');
    }
  };

  const viewTenantDetail = async (tenant) => {
    setSelectedTenant(tenant);
    setTenantDetailOpen(true);
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/tenants/${tenant.id}`);
      setTenantDetail(res.data);
    } catch (error) {
      toast.error('Failed to load tenant details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Database Config
  const saveDbConfig = async (e) => {
    e.preventDefault();
    setSavingDb(true);
    try {
      await axios.post(`${API}/config/databases`, dbForm);
      toast.success('Database configuration added');
      setDbDialogOpen(false);
      setDbForm({ name: '', connection_string: '', database_name: '', is_default: false });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingDb(false);
    }
  };

  const testDbConnection = async (configId) => {
    toast.info('Testing connection...');
    try {
      const res = await axios.post(`${API}/config/databases/${configId}/test`);
      if (res.data.status === 'success') {
        toast.success('Connection successful!');
      } else {
        toast.error(`Connection failed: ${res.data.message}`);
      }
      fetchAllData();
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  const deleteDbConfig = async (config) => {
    if (!confirm(`Delete "${config.name}" configuration?`)) return;
    try {
      await axios.delete(`${API}/config/databases/${config.id}`);
      toast.success('Configuration deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const setDefaultDb = async (config) => {
    if (!confirm(`Set "${config.name}" as the default database? This will be used for all operations.`)) return;
    toast.info('Setting as default...');
    try {
      await axios.post(`${API}/config/databases/${config.id}/set-default`);
      toast.success(`${config.name} is now the default database!`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set as default');
    }
  };

  const migrateToDb = async (targetConfig) => {
    // Find the current default (source)
    const sourceConfig = dbConfigs.find(c => c.is_default);
    if (!sourceConfig) {
      toast.error('No source database found');
      return;
    }
    
    if (targetConfig.id === sourceConfig.id) {
      toast.error('Cannot migrate to the same database');
      return;
    }
    
    if (!confirm(`Migrate ALL data from "${sourceConfig.name}" to "${targetConfig.name}"?\n\nThis will:\n1. Copy all users, transactions, goals, etc.\n2. Overwrite any existing data in the target\n\nThis may take a moment.`)) return;
    
    setMigratingDb(targetConfig.id);
    setMigrationProgress('Starting migration...');
    
    try {
      const res = await axios.post(`${API}/config/databases/${targetConfig.id}/migrate-from/${sourceConfig.id}`);
      
      if (res.data.status === 'success') {
        setMigrationProgress(null);
        toast.success(res.data.message);
        
        // Show detailed results
        const details = res.data.details;
        const summary = Object.entries(details)
          .filter(([_, v]) => v.status === 'success')
          .map(([k, v]) => `${k}: ${v.count}`)
          .join(', ');
        
        toast.info(`Migrated: ${summary}`, { duration: 5000 });
        fetchAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setMigratingDb(null);
      setMigrationProgress(null);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, null, {
        params: {
          current_password: passwordForm.current,
          new_password: passwordForm.new
        }
      });
      toast.success('Password changed successfully!');
      setPasswordDialogOpen(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center shrink-0">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-white truncate">Super Admin</h1>
              <p className="text-xs sm:text-sm text-slate-400 truncate">{admin?.username}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setPasswordDialogOpen(true)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Change Password</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLogout}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Analytics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="bg-slate-800 border-slate-700" data-testid="stat-tenants">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-white">{analytics?.total_tenants || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-400">Tenants</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700" data-testid="stat-users">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-white">{analytics?.total_users || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-400">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700" data-testid="stat-balance">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-500/20 rounded-lg flex items-center justify-center shrink-0">
                  <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-white truncate">${analytics?.total_balance?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs sm:text-sm text-slate-400">Balance</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700" data-testid="stat-transactions">
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-500/20 rounded-lg flex items-center justify-center shrink-0">
                  <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-white">{analytics?.total_transactions || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-400">Trans.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="tenants" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700 w-full flex overflow-x-auto">
            <TabsTrigger value="tenants" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4">
              <Building2 className="w-4 h-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Tenants</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4">
              <UserCog className="w-4 h-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="databases" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4">
              <Database className="w-4 h-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">DBs</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4">
              <Mail className="w-4 h-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4">
              <BarChart3 className="w-4 h-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Stats</span>
            </TabsTrigger>
          </TabsList>

          {/* Tenants Tab */}
          <TabsContent value="tenants">
            <Card className="bg-slate-800 border-slate-700" data-testid="tenants-card">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-6">
                <div>
                  <CardTitle className="text-white text-base sm:text-lg">Tenant Management</CardTitle>
                  <CardDescription className="text-slate-400 text-xs sm:text-sm">Manage family groups</CardDescription>
                </div>
                <Button 
                  onClick={() => openTenantDialog()}
                  className="bg-amber-500 hover:bg-amber-600 self-start sm:self-auto"
                  size="sm"
                  data-testid="create-tenant-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">New Tenant</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {tenants.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No tenants created yet</p>
                    <p className="text-slate-500 text-sm">Create a tenant to organize family groups</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tenants.map((tenant) => (
                      <div 
                        key={tenant.id}
                        className="flex flex-col p-3 sm:p-4 bg-slate-700/50 rounded-xl gap-3"
                        data-testid={`tenant-${tenant.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${tenant.is_active ? 'bg-blue-500/20' : 'bg-slate-600'}`}>
                            <Building2 className={`w-5 h-5 sm:w-6 sm:h-6 ${tenant.is_active ? 'text-blue-400' : 'text-slate-400'}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-medium text-white truncate">{tenant.name}</h3>
                              <Badge variant={tenant.is_active ? "default" : "secondary"} className={`text-xs ${tenant.is_active ? "bg-green-500/20 text-green-400" : ""}`}>
                                {tenant.is_active ? 'Active' : 'Disabled'}
                              </Badge>
                            </div>
                            <p className="text-xs sm:text-sm text-slate-400 truncate">
                              {tenant.banker_count} bankers • {tenant.customer_count} customers • ${tenant.total_balance?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1 sm:gap-2 self-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-white hover:bg-slate-600 px-2"
                            onClick={() => viewTenantDetail(tenant)}
                            data-testid={`view-tenant-${tenant.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-white hover:bg-slate-600 px-2"
                            onClick={() => openTenantDialog(tenant)}
                            data-testid={`edit-tenant-${tenant.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`px-2 ${tenant.is_active ? "text-amber-400 hover:bg-amber-500/20" : "text-green-400 hover:bg-green-500/20"}`}
                            onClick={() => toggleTenantStatus(tenant)}
                            data-testid={`toggle-tenant-${tenant.id}`}
                          >
                            {tenant.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:bg-red-500/20 px-2"
                            onClick={() => deleteTenant(tenant)}
                            data-testid={`delete-tenant-${tenant.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-slate-800 border-slate-700" data-testid="users-card">
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="text-white text-base sm:text-lg">User Management</CardTitle>
                <CardDescription className="text-slate-400 text-xs sm:text-sm">View and assign users to tenants</CardDescription>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {users.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No users found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {users.map((user) => {
                      const userTenant = tenants.find(t => t.id === user.tenant_id);
                      return (
                        <div 
                          key={user.id}
                          className="flex flex-col p-3 sm:p-4 bg-slate-700/50 rounded-xl gap-3"
                          data-testid={`user-${user.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${
                              user.role === 'banker' ? 'bg-blue-500/20' : 'bg-green-500/20'
                            }`}>
                              <Users className={`w-5 h-5 sm:w-6 sm:h-6 ${
                                user.role === 'banker' ? 'text-blue-400' : 'text-green-400'
                              }`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="font-medium text-white truncate">{user.username}</h3>
                                <Badge className={`text-xs ${user.role === 'banker' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                                  {user.role}
                                </Badge>
                                {userTenant && (
                                  <Badge variant="outline" className="border-amber-500 text-amber-400 text-xs">
                                    {userTenant.name}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs sm:text-sm text-slate-400">
                                Balance: ${user.balance?.toFixed(2) || '0.00'}
                                {user.linked_banker_id && ` • Linked`}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-500">Assign to Tenant</span>
                            <Select
                              value={user.tenant_id || "unassigned"}
                              onValueChange={(value) => {
                                if (value !== "unassigned") {
                                  assignUserToTenant(user.id, value);
                                }
                              }}
                            >
                              <SelectTrigger className="w-full bg-slate-700 border-slate-600 text-white">
                                <SelectValue placeholder="Select tenant" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="unassigned" className="text-slate-400">
                                  Unassigned
                                </SelectItem>
                                {tenants.map((tenant) => (
                                  <SelectItem key={tenant.id} value={tenant.id} className="text-white">
                                    {tenant.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Databases Tab */}
          <TabsContent value="databases">
            <Card className="bg-slate-800 border-slate-700" data-testid="databases-card">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-6">
                <div>
                  <CardTitle className="text-white text-base sm:text-lg">Database Connections</CardTitle>
                  <CardDescription className="text-slate-400 text-xs sm:text-sm">Configure external databases</CardDescription>
                </div>
                <Button 
                  onClick={() => setDbDialogOpen(true)}
                  className="bg-amber-500 hover:bg-amber-600 self-start sm:self-auto"
                  size="sm"
                  data-testid="add-db-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Add Connection</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {dbConfigs.length === 0 ? (
                  <div className="text-center py-12">
                    <Database className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No database configurations</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dbConfigs.map((config) => (
                      <div 
                        key={config.id}
                        className="flex flex-col p-3 sm:p-4 bg-slate-700/50 rounded-xl gap-3"
                        data-testid={`db-config-${config.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${
                            config.connection_status === 'connected' ? 'bg-green-500/20' : 
                            config.connection_status === 'error' ? 'bg-red-500/20' : 'bg-slate-600'
                          }`}>
                            <Database className={`w-5 h-5 sm:w-6 sm:h-6 ${
                              config.connection_status === 'connected' ? 'text-green-400' : 
                              config.connection_status === 'error' ? 'text-red-400' : 'text-slate-400'
                            }`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-medium text-white truncate">{config.name}</h3>
                              {config.is_default && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">Default</Badge>
                              )}
                              <Badge variant="outline" className={`text-xs ${
                                config.connection_status === 'connected' ? 'border-green-500 text-green-400' : 
                                config.connection_status === 'error' ? 'border-red-500 text-red-400' : 'border-slate-500 text-slate-400'
                              }`}>
                                {config.connection_status}
                              </Badge>
                            </div>
                            <p className="text-xs sm:text-sm text-slate-400 font-mono truncate">
                              {config.connection_string_masked} / {config.database_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1 sm:gap-2 self-end flex-wrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-400 hover:bg-blue-500/20 px-2"
                            onClick={() => testDbConnection(config.id)}
                            data-testid={`test-db-${config.id}`}
                            title="Test Connection"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          {!config.is_default && config.connection_status === 'connected' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-purple-400 hover:bg-purple-500/20 px-2"
                                onClick={() => migrateToDb(config)}
                                disabled={migratingDb === config.id}
                                data-testid={`migrate-db-${config.id}`}
                                title="Migrate data to this database"
                              >
                                {migratingDb === config.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <ArrowRightLeft className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-amber-400 hover:bg-amber-500/20 px-2"
                                onClick={() => setDefaultDb(config)}
                                data-testid={`set-default-db-${config.id}`}
                                title="Set as default"
                              >
                                <Star className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {!config.is_default && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:bg-red-500/20 px-2"
                              onClick={() => deleteDbConfig(config)}
                              data-testid={`delete-db-${config.id}`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Configuration Tab */}
          <TabsContent value="email">
            <EmailConfig token={localStorage.getItem('admin_token')} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700" data-testid="user-analytics">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-400" />
                    User Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Total Bankers</span>
                    <span className="text-white font-medium">{analytics?.total_bankers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Total Customers</span>
                    <span className="text-white font-medium">{analytics?.total_customers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Active Tenants</span>
                    <span className="text-white font-medium">{analytics?.active_tenants || 0}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700" data-testid="transaction-analytics">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-400" />
                    Transaction Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Pending</span>
                    <Badge className="bg-amber-500/20 text-amber-400">{analytics?.pending_transactions || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Approved</span>
                    <Badge className="bg-green-500/20 text-green-400">{analytics?.approved_transactions || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Rejected</span>
                    <Badge className="bg-red-500/20 text-red-400">{analytics?.rejected_transactions || 0}</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Tenant Dialog */}
      <Dialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="tenant-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingTenant ? 'Update tenant details' : 'Create a new family group'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveTenant} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Tenant Name</Label>
              <Input
                data-testid="tenant-name-input"
                value={tenantForm.name}
                onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                placeholder="e.g., Smith Family"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description (optional)</Label>
              <Input
                data-testid="tenant-description-input"
                value={tenantForm.description}
                onChange={(e) => setTenantForm({ ...tenantForm, description: e.target.value })}
                placeholder="Brief description"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-amber-500 hover:bg-amber-600"
              disabled={savingTenant}
              data-testid="save-tenant-btn"
            >
              {savingTenant ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingTenant ? 'Update Tenant' : 'Create Tenant'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Database Config Dialog */}
      <Dialog open={dbDialogOpen} onOpenChange={setDbDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="db-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Add Database Connection</DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure a new database connection (e.g., Azure Cosmos DB)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveDbConfig} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Display Name</Label>
              <Input
                data-testid="db-name-input"
                value={dbForm.name}
                onChange={(e) => setDbForm({ ...dbForm, name: e.target.value })}
                placeholder="e.g., Azure Cosmos DB"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Connection String</Label>
              <Input
                data-testid="db-connection-input"
                type="password"
                value={dbForm.connection_string}
                onChange={(e) => setDbForm({ ...dbForm, connection_string: e.target.value })}
                placeholder="mongodb://..."
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Database Name</Label>
              <Input
                data-testid="db-database-input"
                value={dbForm.database_name}
                onChange={(e) => setDbForm({ ...dbForm, database_name: e.target.value })}
                placeholder="tinybank"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-amber-500 hover:bg-amber-600"
              disabled={savingDb}
              data-testid="save-db-btn"
            >
              {savingDb ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
              Add Connection
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tenant Detail Dialog */}
      <Dialog open={tenantDetailOpen} onOpenChange={setTenantDetailOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl" data-testid="tenant-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              {selectedTenant?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedTenant?.description || 'No description'}
            </DialogDescription>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : tenantDetail && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{tenantDetail.bankers?.length || 0}</p>
                  <p className="text-sm text-slate-400">Bankers</p>
                </div>
                <div className="bg-slate-700/50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{tenantDetail.customers?.length || 0}</p>
                  <p className="text-sm text-slate-400">Customers</p>
                </div>
                <div className="bg-slate-700/50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">${tenantDetail.total_balance?.toFixed(2) || '0.00'}</p>
                  <p className="text-sm text-slate-400">Total Balance</p>
                </div>
              </div>
              
              {tenantDetail.bankers?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Bankers</h4>
                  <div className="space-y-2">
                    {tenantDetail.bankers.map((b) => (
                      <div key={b.id} className="flex items-center justify-between bg-slate-700/30 p-3 rounded-lg">
                        <span className="text-white">{b.username}</span>
                        <span className="text-slate-400">${b.balance?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {tenantDetail.customers?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Customers</h4>
                  <div className="space-y-2">
                    {tenantDetail.customers.map((c) => (
                      <div key={c.id} className="flex items-center justify-between bg-slate-700/30 p-3 rounded-lg">
                        <span className="text-white">{c.username}</span>
                        <span className="text-slate-400">${c.balance?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" />
              Change Password
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update your super admin password
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Current Password</Label>
              <Input
                data-testid="current-password-input"
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                placeholder="Enter current password"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">New Password</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                placeholder="Enter new password (min 8 characters)"
                className="bg-slate-700 border-slate-600 text-white"
                required
                minLength={8}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Confirm New Password</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                placeholder="Confirm new password"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-amber-500 hover:bg-amber-600"
              disabled={changingPassword}
              data-testid="save-password-btn"
            >
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Update Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
